import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Users, Euro, MoreHorizontal, FileText, Loader2, Edit, Copy, Trash2, Eye, Plane, Mail, MessageSquareText, Pencil } from "lucide-react";
import { cn, formatTravelDates } from "@/components/utils/utils";
import { DashboardItinerary } from "@/lib/models/dashboard";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { getAvatarColor, getInitials } from "@/components/utils/avatar-colors";
import { useItineraryPDF } from "@/hooks/useItineraryPDF";
import { toast } from "@/hooks/useToast";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { ItineraryStatusBadge } from "./ItineraryStatusBadge";

import { DashboardTravelRequest } from "@/lib/models/dashboard";
import { createClient } from "@supabase/supabase-js";
import { SUPABASE_KEY, SUPABASE_URL } from "@/components/utils/constants";
import { ItineraryStatus } from "../builder/models/itinerary.model";
import { useLanguage } from "@/contexts/LanguageContext";
import { useModal } from "@/contexts/ModalContext";
import { getStorageThumbnailUrl } from "@/utils/imageStorage";

interface ItineraryCardProps {
  itinerary: DashboardItinerary;
  className?: string;
  onEdit?: (itineraryId: string) => void;
  onDelete?: (itineraryId: string) => void;
  onDuplicate?: (itineraryId: string) => void;
  onViewTravelRequest?: (travelRequestId: string) => void;
  onViewItinerary?: (itineraryId: string) => void;
  onUpdateStatus?: (itineraryId: string, status: ItineraryStatus) => void;
  travelRequests?: DashboardTravelRequest[];
  isAdmin?: boolean;
  isTraveler?: boolean;
  userRole?: string;
}

export const ItineraryCard = ({
  itinerary,
  className,
  onEdit,
  onDelete,
  onDuplicate,
  onViewTravelRequest,
  onViewItinerary,
  onUpdateStatus,
  isAdmin,
  isTraveler,
  userRole,
}: ItineraryCardProps) => {
  const { t } = useLanguage();
  const { openModal, currentModal } = useModal();
  const { generatePDF } = useItineraryPDF();
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleCreateTripQuote = () => {
    openModal('create-quote', {
      onSend: handleSendItineraryInReview
    });
  };

  const handleViewNotes = () => {
    openModal('notes', {
      note: itinerary.note
    });
  };

  const handleCardClick = () => {
    // Prevent opening details modal if any modal is currently open
    if (currentModal) return;
    
    openModal('itinerary-details', {
      itinerary,
      onViewItinerary: handleViewItinerary
    });
  };

  // Handler per aprire la nuova modale
  const handleSendQuoteModal = () => {
    openModal('send-quote', {
      itinerary,
      onSendQuote: handleSendQuote
    });
  };

  const handleViewTravelRequest = () => {
    onViewTravelRequest(itinerary.travelRequest.id);
  };

  const handleViewItinerary = () => {
    if (!onViewItinerary) return;
    onViewItinerary(itinerary.id);
  };

  const handleEdit = () => {
    if (!onEdit) return;
    onEdit(itinerary.id);
  };

  const handleDuplicate = () => {
    if (!onDuplicate) return;
    onDuplicate(itinerary.id);
  };

  const handleDelete = () => {
    if (!onDelete) return;
    onDelete(itinerary.id);
  };

  const handleSendQuote = async (payment: string) => {
    // Chiama la edge function send-quote su Supabase
    try {
      setIsGeneratingPDF(true);

      // let pdfBase64: string | null = null;
      // let pdfFilename: string | null = null;

      // if (process.env.NODE_ENV !== 'development') {
      //   const pdfResult = await generatePDF(itinerary.id, false);
      //   pdfBase64 = pdfResult.pdf.output('datauristring').split(',')[1];
      //   if (!pdfBase64 || !pdfResult?.fileName) {
      //     toast({
      //       title: t('errors.generic'),
      //       description: t('itinerary.pdfConversionError'),
      //       variant: "destructive",
      //     });
      //     setIsGeneratingPDF(false);
      //     return false;
      //   }
      //   pdfFilename = pdfResult.fileName;
      // }

      const draftId = itinerary.id;
      const to = itinerary.contactEmail;
      const subject = `Your Travel Quote Is Ready`;


      const supabase = createClient(SUPABASE_URL, SUPABASE_KEY)
      const { data, error } = await supabase.functions.invoke("send-quote", {
        body: JSON.stringify({
          draftId,
          // pdfBase64,
          // pdfFilename,
          to,
          subject,
          variables: {
            client_first_name: itinerary?.travelRequest.contact.firstName,
            client_last_name: itinerary?.travelRequest.contact.lastName,
            itinerary_title: itinerary.title,
            start_date: itinerary.startDate,
            end_date: itinerary.endDate,
            payment
          },
        }),
      });

      setIsGeneratingPDF(false);
      if (error) {
        toast({
          title: t('itinerary.sendQuoteError'),
          description: t('itinerary.sendQuoteErrorDesc'),
          variant: "destructive",
        });
        return false;
      }

      toast({
        title: t('itinerary.quoteSent'),
        description: t('itinerary.quoteSentDesc'),
      });
      // Aggiorna lo stato a sended dopo l'invio
      if (onUpdateStatus) {
        await onUpdateStatus(itinerary.id, 'sended');
      }
      return true;
    } catch (error) {
      setIsGeneratingPDF(false);
      console.error('Errore invio preventivo:', error);
      toast({
        title: t('errors.generic'),
        description: t('itinerary.sendQuoteErrorGeneric'),
        variant: "destructive",
      });
      return false;
    }
  };

  const handleSendItineraryInReview = async (): Promise<boolean> => {
    try {
      if (onUpdateStatus) {
        await onUpdateStatus(itinerary.id, 'pending');
        toast({
          title: t('itinerary.requestSent'),
          description: t('itinerary.requestSentDesc'),
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Errore invio richiesta:', error);
      toast({
        title: t('errors.generic'),
        description: t('itinerary.sendRequestError'),
        variant: "destructive",
      });
      return false;
    }
  };

  const handleGeneratePDF = async () => {
    if (isGeneratingPDF) return; // Prevent multiple clicks

    setIsGeneratingPDF(true);
    try {
      await generatePDF(itinerary.id, true);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: t('errors.generic'),
        description: t('itinerary.pdfGenerationError'),
        variant: "destructive",
      });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Get guest count
  const guestCount = itinerary.totalGuests || 1;

  return (
    <Card
      className={cn(
        "border border-gray-300 rounded-2xl overflow-hidden transition-all cursor-pointer",
        (isDropdownOpen) ? "-translate-y-2 scale-105 shadow-xl" : "hover:-translate-y-2 hover:scale-105 hover:shadow-xl",
        className
      )}
      onClick={handleCardClick}
    >
      <CardContent className="p-0 h-full flex flex-col">
        {/* Image Header */}
        <div className="relative w-full h-[150px]">
          {/* Background Image */}
          <div
            className={cn(
              "absolute inset-0 bg-gray-200 bg-cover bg-center transition-all rounded-tl rounded-tr",
              itinerary.isBlurred && "blur-[2px] brightness-90"
            )}
            style={{
              backgroundImage: itinerary.image
                ? `url(${getStorageThumbnailUrl(itinerary.image, { noTransform: true })})`
                : undefined
            }}
          />

          {/* Badge e messaggi per status */}
          <div>
            <div className="absolute top-3 left-3 flex flex-col gap-2">

              <ItineraryStatusBadge status={itinerary.detailedStatus} hideDraftQuote={true} userRole={userRole} />
            </div>
            <div className="absolute bottom-3 left-3 flex flex-col gap-2">

              {/* Bottone review solo per pending e admin */}
              {itinerary.isPending && isAdmin && (
                <div className="flex gap-2 mt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-gray-500 bg-gray-100 hover:bg-gray-200"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit();
                    }}
                  >
                    {t('itinerary.takeReview')}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="absolute top-3 right-3 flex gap-2">

            {itinerary.note && (!isTraveler || itinerary.status === 'request_changes') && (
              <Button
                variant="ghost"
                size="sm"
                className="w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white/95"
                title="View Notes"
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewNotes();
                }}
              >
                <MessageSquareText className="h-4 w-4 text-gray-800" />
              </Button>
            )}

            {/* Menu Button */}
            <DropdownMenu onOpenChange={setIsDropdownOpen}>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white/95"
                  title="Menu"
                >
                  <MoreHorizontal className="h-5 w-5 text-gray-800" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {isTraveler && (
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleViewItinerary(); }}>
                    <Eye className="h-4 w-4 mr-2" />
                    {t('itinerary.viewItinerary')}
                  </DropdownMenuItem>
                )}

                {!isTraveler && (
                  <>
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleViewItinerary(); }}>
                      <Pencil className="h-4 w-4 mr-2" />
                      {t('itinerary.editItinerary')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleViewTravelRequest(); }}>
                      <Eye className="h-4 w-4 mr-2" />
                      {t('travelRequest.viewTripRequest')}
                    </DropdownMenuItem>
                    {isAdmin && (
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); window.open(`https://www.backoffice.place2be.it/itineraries/edit/${itinerary.id}`, '_blank'); }}>
                        <Edit className="h-4 w-4 mr-2" />
                        {t('itinerary.openInBackoffice')}
                      </DropdownMenuItem>
                    )}
                    {itinerary.status === 'draft' && (
                      <>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleCreateTripQuote(); }}>
                          <FileText className="h-4 w-4 mr-2" />
                          {t('itinerary.submitForApproval')}
                        </DropdownMenuItem>

                      </>
                    )}
                  </>
                )}



                {!isTraveler && (
                  <>
                    <DropdownMenuSeparator />

                    {itinerary.status === 'approved' && (
                      <>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleSendQuoteModal(); }}>
                          <Mail className="h-4 w-4 mr-2" />
                          {t('itinerary.sendToClient')}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}


                  </>
                )}

                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleGeneratePDF(); }} disabled={isGeneratingPDF}>
                  {isGeneratingPDF ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <FileText className="h-4 w-4 mr-2" />
                  )}
                  {isGeneratingPDF ? t('itinerary.generatingPDF') : t('itinerary.generatePDF')}
                </DropdownMenuItem>

                {!isTraveler && (itinerary.status !== 'sended' && itinerary.status !== 'completed') && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDuplicate(); }}>
                      <Copy className="h-4 w-4 mr-2" />
                      {t('common.duplicate')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDelete(); }}>
                      <Trash2 className="h-4 w-4 mr-2" />
                      {t('common.delete')}
                    </DropdownMenuItem>
                  </>
                )}



              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-3 flex flex-col">
          {/* Title */}
          <h3 className="text-lg font-semibold text-black mb-4 line-clamp-1">
            {itinerary.title || t('itinerary.untitledItinerary')}
          </h3>

          {/* Trip Info Row */}
          <div className="flex items-center justify-between mb-4">
            {/* Dates */}
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-600" />
              <span className="text-xs text-gray-600">
                {formatTravelDates(itinerary.startDate, itinerary.endDate)}
              </span>
            </div>

            {/* People */}
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-gray-600" />
              <span className="text-sm text-gray-600">
                {guestCount}
              </span>
            </div>

            {/* Total */}
            <div className="flex items-center gap-2">
              <Euro className="h-4 w-4 text-gray-600" />
              <span className="text-sm text-gray-600">
                {itinerary.total}
              </span>
            </div>
          </div>

          {/* User Info */}
          {itinerary && (
            <div className="flex items-center justify-between gap-2">
              {/* Contact info */}
              {isTraveler ? (
                itinerary.assignedAdvisor ? (
                  <div className="flex items-center gap-2">
                    <Avatar className="h-6 w-6">
                      <AvatarFallback className={`text-xs text-white ${getAvatarColor(itinerary.assignedAdvisor.email)}`}>
                        {getInitials(itinerary.assignedAdvisor.firstName, itinerary.assignedAdvisor.lastName)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-xs text-gray-900 font-medium">
                      by {itinerary.assignedAdvisor.firstName} {itinerary.assignedAdvisor.lastName}
                    </span>
                  </div>
                ) : (
                  <span className="text-xs text-gray-400">No designer assigned</span>
                )
              ) : (
                <div className="flex items-center gap-2">
                  <Avatar className="h-6 w-6">
                    <AvatarFallback className={`text-xs text-white ${getAvatarColor(itinerary.contactEmail)}`}>
                      {getInitials(itinerary.contactFullName.split(' ')[0], itinerary.contactFullName.split(' ').at(-1))}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-xs text-gray-900 font-medium">
                    {itinerary.contactFullName}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
